import { z } from 'zod';

/**
 * Validation schemas for ADDAPT forms using Zod
 */

// Profile creation schema based on API requirements
export const profileCreationSchema = z.object({
  profileName: z
    .string()
    .min(1, 'Profile name is required')
    .max(255, 'Profile name must be 255 characters or less'),
  
  ldap: z
    .string()
    .min(1, 'LDAP group is required')
    .max(255, 'LDAP group must be 255 characters or less'),
  
  description: z
    .string()
    .min(1, 'Description is required')
    .max(1000, 'Description must be 1000 characters or less')
});

// Type inference from schema
export type ProfileCreationInput = z.infer<typeof profileCreationSchema>;

// Job creation schema
export const jobCreationSchema = z.object({
  jobName: z
    .string()
    .min(1, 'Job name is required')
    .max(255, 'Job name must be 255 characters or less'),
  
  jobId: z
    .string()
    .optional(),
  
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less'),
  
  jobType: z
    .enum(['DRAFT', 'RELEASE'])
    .optional()
});

export type JobCreationInput = z.infer<typeof jobCreationSchema>;

// Job edit/update schema
export const jobUpdateSchema = z.object({
  name: z
    .string()
    .min(1, 'Job name is required')
    .max(255, 'Job name must be 255 characters or less')
    .trim(),
  
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .optional()
    .or(z.literal('')),
  
  type: z
    .enum(['ondemand', 'scheduled'])
    .optional(),
  
  scheduleTime: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(val);
    }, 'Invalid time format (HH:mm)'),
  
  scheduleDate: z
    .string()
    .optional()
});

export type JobUpdateInput = z.infer<typeof jobUpdateSchema>;

// Helper function to format Zod errors for display
export function formatZodErrors(error: z.ZodError): Record<string, string> {
  const formattedErrors: Record<string, string> = {};
  
  error.errors.forEach((err) => {
    const path = err.path.join('.');
    formattedErrors[path] = err.message;
  });
  
  return formattedErrors;
}

// Validation helper with better error messages
export function validateWithSchema<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  try {
    const validData = schema.parse(data);
    return { success: true, data: validData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { success: false, errors: formatZodErrors(error) };
    }
    throw error;
  }
}


// ============================================
// Schedule Management Validation Schemas
// ============================================

/**
 * AWS EventBridge cron expression validation
 * Format: minute hour day-of-month month day-of-week year
 * 
 * Valid values:
 * - minute: 0-59
 * - hour: 0-23
 * - day-of-month: 1-31, ?, *, L, W
 * - month: 1-12 or JAN-DEC
 * - day-of-week: 1-7 or SUN-SAT, ?, *, L, #
 * - year: 1970-2199 or *
 */
const cronExpressionRegex = /^(\*|[0-5]?\d|\d-\d|\d\/\d|\d,\d+)\s+(\*|[01]?\d|2[0-3]|\d-\d|\d\/\d|\d,\d+)\s+(\*|\?|[1-9]|[12]\d|3[01]|L|W|\d-\d|\d\/\d|\d,\d+)\s+(\*|[1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|\d-\d|\d\/\d|\d,\d+)\s+(\*|\?|[1-7]|SUN|MON|TUE|WED|THU|FRI|SAT|L|#\d|\d-\d|\d\/\d|\d,\d+)\s+(\*|\d{4}|\d{4}-\d{4})$/i;

/**
 * Schedule creation/update schema
 * Requirement 10.4: Form validation with field-level error highlighting
 */
export const scheduleFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Schedule name is required')
    .max(100, 'Name must be 100 characters or less')
    .trim(),
  
  description: z
    .string()
    .max(500, 'Description must be 500 characters or less')
    .optional()
    .or(z.literal('')),
  
  cronExpression: z
    .string()
    .min(1, 'Cron expression is required')
    .refine(
      (val) => {
        // Basic validation - more thorough validation done by cron-parser
        const parts = val.trim().split(/\s+/);
        return parts.length === 6;
      },
      'Invalid cron expression. AWS EventBridge cron requires 6 fields: minute hour day-of-month month day-of-week year'
    ),
  
  startDate: z
    .string()
    .min(1, 'Start date is required')
    .refine(
      (val) => {
        const date = new Date(val);
        return !isNaN(date.getTime());
      },
      'Invalid date format'
    ),
  
  endDate: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => {
        if (!val) return true;
        const date = new Date(val);
        return !isNaN(date.getTime());
      },
      'Invalid date format'
    )
}).refine(
  (data) => {
    // End date must be after start date if provided
    if (!data.endDate) return true;
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    return end > start;
  },
  {
    message: 'End date must be after start date',
    path: ['endDate']
  }
);

export type ScheduleFormInput = z.infer<typeof scheduleFormSchema>;

/**
 * Validate schedule form data
 * @param data - Form data to validate
 * @returns Validation result with errors if any
 */
export function validateScheduleForm(
  data: unknown
): { success: true; data: ScheduleFormInput } | { success: false; errors: Record<string, string> } {
  return validateWithSchema(scheduleFormSchema, data);
}

/**
 * Individual field validators for real-time validation
 */
export const scheduleFieldValidators = {
  /**
   * Validate schedule name
   */
  name: (value: string): string | null => {
    if (!value.trim()) return 'Schedule name is required';
    if (value.length > 100) return 'Name must be 100 characters or less';
    return null;
  },

  /**
   * Validate cron expression format
   * Note: Full validation should use cron-parser library
   */
  cronExpression: (value: string): string | null => {
    if (!value.trim()) return 'Cron expression is required';
    const parts = value.trim().split(/\s+/);
    if (parts.length !== 6) {
      return 'Invalid cron expression. AWS EventBridge cron requires 6 fields';
    }
    return null;
  },

  /**
   * Validate start date
   */
  startDate: (value: string): string | null => {
    if (!value) return 'Start date is required';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'Invalid date format';
    return null;
  },

  /**
   * Validate end date (optional, but must be after start date if provided)
   */
  endDate: (value: string, startDate: string): string | null => {
    if (!value) return null; // Optional field
    const end = new Date(value);
    if (isNaN(end.getTime())) return 'Invalid date format';
    
    if (startDate) {
      const start = new Date(startDate);
      if (end <= start) return 'End date must be after start date';
    }
    return null;
  },

  /**
   * Validate description (optional)
   */
  description: (value: string): string | null => {
    if (value && value.length > 500) return 'Description must be 500 characters or less';
    return null;
  }
};
